<template>
  <v-row no-gutters class="fill-height pt-5">
    <v-col v-if="isFetched" cols="12">
      <card-title-row
        :has-icon-column="true"
        translation-path="sidebar.zdmCustomReports.title"
        :amount="1"
        :is-plural="true"
      >
        <template #subtitle>
          <italic-subheader
            style="text-align: left; max-width: 20vw"
            translation-path="sidebar.zdmCustomReports.selectReportSubtitle"
          ></italic-subheader>
        </template>
      </card-title-row>
      <sidebar-row>
        <zdm-custom-reports-list :reports="customReports" :selected-report.sync="selectedReport" />
      </sidebar-row>
      <card-title-row
        :has-icon-column="true"
        :straight-title="isReportSelected ? selectedReport.name : ''"
        class="pt-4 font-weight-bold"
        customSize="h6"
        :amount="1"
        :is-plural="true"
      >
        <template #subtitle>
          <italic-subheader
            style="text-align: left; max-width: 20vw"
            :straight-text="isReportSelected ? selectedReport.description : ''"
          ></italic-subheader>
        </template>
      </card-title-row>
      <card-title-row
        :has-icon-column="true"
        is-subtitle
        translation-path="sidebar.zdmCustomReports.additionalParams"
        :amount="1"
        :is-plural="true"
      >
        <template #postTitle>
          <v-subheader
            style="font-style: italic; font-size: 13px; opacity: 0.8; padding-left: 0.3rem"
            v-text="$i18n.t('sidebar.zdmCustomReports.optional')"
          ></v-subheader>
        </template>
      </card-title-row>
      <sidebar-row class="pt-2">
        <sidebar-select-row
          font-size="14px"
          v-model="district"
          :select-props="districtProps"
          translation-path="sidebar.zdmCustomReports.district"
          align="center"
        />
      </sidebar-row>
      <sidebar-row class="pt-4">
        <v-checkbox v-model="includeMap">
          <template #label>
            <div class="checkbox-text">
              {{ $i18n.t('sidebar.zdmCustomReports.includeMap') }}
            </div>
          </template>
        </v-checkbox>
      </sidebar-row>
    </v-col>
    <v-col v-else>
      <main-loader />
    </v-col>
  </v-row>
</template>

<script>
import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import ZdmCustomReportsList from '@/components/ZDMCustomReportsList.vue';
import SidebarRow from '@/components/SidebarRow';
import SidebarSelectRow from '@/components/SidebarSelectRow';
import MainLoader from '@/components/MainLoader';

import { sync, call, get } from 'vuex-pathify';

export default {
  name: 'ZDMCustomReportsSidebar',
  components: {
    CardTitleRow,
    ItalicSubheader,
    SidebarRow,
    SidebarSelectRow,
    ZdmCustomReportsList,
    MainLoader,
  },
  data: () => ({
    isFetched: false,
    district: undefined,
    districtName: undefined,
    datasource: undefined,
    includeMap: false,
    features: undefined,
    selectedReport: {},
    currentTaskId: null,
  }),
  computed: {
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
    dataSources: get('layers/metadata'),
    customReports: get('zdmCustomReports/reports'),
    selectedFeatures: get('map/selectedFeatures'),
    isReportSelected() {
      return Object.keys(this.selectedReport).length > 0;
    },
    selectedDatasource() {
      return this.dataSources?.[this.datasource];
    },
    attributesSchema() {
      return this.selectedDatasource?.attributes_schema;
    },
    computedFeatures() {
      return this.features === undefined
        ? []
        : this.features?.features.map(value => {
            return {
              [this.attributesSchema?.id_name]: value.id.toString(),
              district_id: value.id,
              ...value.properties,
            };
          });
    },
    districtProps() {
      return {
        items: this.computedFeatures,
        itemValue: this.districtName,
        itemText: this.districtName,
        autoConvertSelect: false,
        hideDetails: true,
        clearable: true,
      };
    },
  },
  methods: {
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getCustomReports: call('zdmCustomReports/getCustomReports'),
    getCustomReportId: call('zdmCustomReports/getCustomReportId'),
    checkCustomReportStatus: call('celeryTasks/getCeleryTaskStatus'),
    generateCustomReport: call('celeryTasks/getCeleryTaskResult'),
    cancelCustomReport: call('celeryTasks/stopCeleryTask'),
    async generateReport() {
      try {
        this.isStickyButtonsLoading = true;
        const taskId = await this.getCustomReportId({
          id: this.selectedReport.id,
          payload: {
            with_map: this.includeMap,
            district: this.district ? this.district : undefined,
            ids: this.selectedFeatures[this.selectedReport.layer]
              ? this.selectedFeatures[this.selectedReport.layer]
              : [],
          },
        });
        this.currentTaskId = taskId;
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.generatingReport'),
        });
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            const status = await this.checkCustomReportStatus(taskId);
            if (['REVOKED', 'FAILURE'].includes(status)) {
              this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
              return 'cancel';
            }
            return status === 'SUCCESS';
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            await this.generateCustomReport({ id: taskId });
            this.currentTaskId = null;
            this.isStickyButtonsLoading = false;
          },
          interval: 5000,
        });
      } catch (error) {
        this.loading = false;
        this.currentTaskId = null;
        console.log('error', error);
      }
    },
    async init() {
      this.isStickyButtonsLoading = false;
      this.isStickyButtonsValid = false;
      this.isStickyButtonsVisible = true;
      try {
        await this.getCustomReports();
        this.$root.$off('generateReport');
        this.$root.$on('generateReport', this.generateReport);
        this.stickyButtons = [
          {
            action: 'generateReport',
            translationPath: 'sidebar.zdmCustomReports.generateReport',
          },
        ];
        const r = await this.getDatasourceSettings('zdm_reports_district');
        if (r !== null) {
          this.datasource = r.datasource;
          this.districtName = r.settings.district_name;
          if (this.datasource) {
            this.features = await this.getDataSourceFeatures({
              dataSource: this.datasource,
              with_geometry: false,
            });
          }
        }
        this.isFetched = true;
      } catch {
        this.isFetched = true;
      }
    },
  },
  watch: {
    selectedReport() {
      this.isReportSelected ? (this.isStickyButtonsValid = true) : (this.isStickyButtonsValid = false);
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsValid = false;
    this.isStickyButtonsVisible = false;
    if (this.currentTaskId) {
      this.cancelCustomReport(this.currentTaskId);
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep .checkbox-text {
  color: #202124;
  font-style: normal;
  font-weight: normal;
}
</style>
